import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';

import styles from './AddProductDetail.module.scss';
import { IAddProductDetailProps } from './IAddProductDetailProps';
import { ProductDetailsCreateRequestModel } from '../../../models/productDetails/ProductDetailsCreateRequestModel';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { MultiSelect } from 'primereact/multiselect';

const AddProductDetail = ({
	parentProductDetailId,
	parentProductDetailDescription,
	setShouldReload
}: IAddProductDetailProps): JSX.Element => {
	const [description, setDescription] = useState<string>('');
	const [divisionsIds, setDivisionsIds] = useState<[number] | undefined>(
		undefined
	);
	const [productTypeId, setProductTypeId] = useState<number | undefined>(
		undefined
	);

	const [divisionsOptions, setDivisionOptions] = useState<DropdownOption[]>(
		[]
	);
	const [productTypesOptions, setProductTypesOptions] = useState<
		DropdownOption[]
	>([]);

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>(`productTypes/dropdown`, undefined)
			.then((options) => setProductTypesOptions(options))
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>(`divisions/dropdown`, undefined)
			.then((options) => setDivisionOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	const resetDialog = (): void => {
		setDivisionsIds(undefined);
		setProductTypeId(undefined);
		setIsVisible(false);
		setIsLoading(false);
		document.body.classList.remove('no-scroll');
	};

	const handleAdd = async (): Promise<void> => {
		if (!description || !productTypeId) {
			return;
		}

		setIsLoading(true);

		const productDetail: ProductDetailsCreateRequestModel = {
			description,
			divisionsIds,
			productTypeId,
			parentProductDetailId,
		};

		try {
			await httpService.post(
				'productDetails',
				JSON.stringify(productDetail),
				undefined,
				false
			);

			setShouldReload(true);
			resetDialog();
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	const addDialogFooter: JSX.Element = (
		<>
			<button
				className='btn primary'
				onClick={handleAdd}
				disabled={!description || !productTypeId}
			>
				Add
			</button>
			<button className='btn secondary' onClick={resetDialog}>
				Cancel
			</button>
		</>
	);

	const pendingFooter: JSX.Element = (
		<div className={styles.dialogFooter}>
			<p>Creating Product Detail...</p>
			<ProgressSpinner />
		</div>
	);

	return (
		<>
			<button
				className={`${styles.addButton} btn primary`}
				onClick={() => {
					setIsVisible(true);
					document.body.classList.add('no-scroll');
				}}
			>
				Add
			</button>

			<Dialog
				className={styles.dialog}
				header='Add Product Detail'
				visible={isVisible}
				onHide={resetDialog}
				footer={isLoading ? pendingFooter : addDialogFooter}
				baseZIndex={9999}
			>
				<div className={styles.dialogContent}>
					<label htmlFor='description'>Description</label>
					<InputText
						id='description'
						placeholder='Description'
						value={description}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							setDescription(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='parentProductDetailDescription'>
						Parent Product Detail
					</label>
					<InputText
						disabled
						id='parentProductDetailDescription'
						placeholder='Parent Product Detail'
						value={parentProductDetailDescription ?? ''}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='divisionId'>Divisions</label>
					<MultiSelect
						id='divisionId'
						required
						placeholder='Select Division'
						value={divisionsIds}
						options={divisionsOptions}
						onChange={(e: DropdownChangeParams) => {
							setDivisionsIds(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='productTypeId'>Product type</label>
					<Dropdown
						id='productTypeId'
						required
						filter
						placeholder='Select Product type'
						value={productTypeId}
						options={productTypesOptions}
						onChange={(e: DropdownChangeParams) => {
							setProductTypeId(e.target.value);
						}}
					/>
				</div>
			</Dialog>
		</>
	);
};

export default AddProductDetail;
